import {forkJoin, Observable, of} from 'rxjs';
import {map, mergeMap, switchMap, tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {IStoreDesignation} from '../designations/designation';
import {IStoreType} from '../store-types/store-types';
import {IResponse} from '../../batches/batches.service';
import {INewStore, IStore, IStoreBulkOperationRequest, IStoreBulkOperationsParams} from '../store';
import {ProjectsService} from '../../batches/projects/projects.service';
import {MIHttpClientService} from 'src/app/shared/mi-http-client.service';
import {ICountry} from 'src/app/shared/components/address/country';
import {IBulkOperationSelection} from 'src/app/shared/list-view/list-view.component';
import {StoreDesignationService} from './store-designation.service';
import {HttpParams} from '@angular/common/http';
import {CountryStatesService, IState} from '../../shared/country-states-service';
import {IdName} from '../../shared/common-interfaces';
import {isNil} from 'lodash-es';
import {StoreTypeService} from './store-type.service';
import {sortCaseInsensitive} from '../../shared/custom-rxjs-operators';
import {IProject} from 'src/app/batches/projects/project';
import {UsersService} from 'src/app/users/users.service';


@Injectable({
  providedIn: 'root',
})
export class StoresService {

  constructor(
    private projectsService: ProjectsService,
    private countryStatesService: CountryStatesService,
    private storeDesignationsService: StoreDesignationService,
    private storeTypeService: StoreTypeService,
    private usersService: UsersService,
    private miClient: MIHttpClientService,
  ) {
  }

  filterStoresByNameNumberOrNickname(name): Observable<IResponse> {
    const storesUrl = 'stores?storeNameOrNumberOrNickname=' + encodeURIComponent(name);

    if (name === null || typeof name !== 'string') {
      return of({ list: [], TotalRowCount: 0 });
    }
    return this.miClient.get<IResponse>(storesUrl)
      .pipe(
        tap((response: IResponse) => {
          return response.list;
        }),
      );
  }

  createStore(item: INewStore, assignedProjects?: number[]): Observable<any> {

    return this.miClient.post('stores', item)
      .pipe(
        mergeMap(result => {
          if (result['storeId'] && assignedProjects && assignedProjects.length > 0) {
            return this.projectsService.assignStoreToProjects(result['storeId'], assignedProjects).pipe(
              map(
                () => result,
              ),
            );
          } else {
            return of(result);
          }
        }),
      );
  }

  updateStore(item: any, assignedProjects?: number[]): Observable<any> {
    return this.miClient.post('stores/' + item.id, item)
      .pipe(
        mergeMap(() => {
          if (assignedProjects) {
            return this.projectsService.assignStoreToProjects(item.id, assignedProjects);
          } else {
            return of(null);
          }
        }),
      );
  }

  updateStoresBulk(bulkOperationParams: IStoreBulkOperationsParams): Observable<any> {
    let urlFilters = '';
    if (bulkOperationParams.filters.length > 0) {
      urlFilters = `${bulkOperationParams.filters}`;
    }
    const obj: IStoreBulkOperationRequest = {
      bulkOperationType: 'UPDATE',
      updateFields: bulkOperationParams.bulkUpdateFields,
    };
    if (bulkOperationParams.items.length > 0) {
      obj.storeIds = bulkOperationParams.items.map(item => item.id);
    }
    return this.miClient.post('stores/bulkOperation' + urlFilters, obj);
  }

  deleteStore(id: number): Observable<any> {
    return this.miClient.delete('stores/' + `${id}`);
  }

  undeleteStore(id: number): Observable<any> {
    return this.miClient.get('stores/undeleteStore/' + `${id}`);
  }

  deleteStoresBulk(bulkOperationParams: IBulkOperationSelection<IStore>): Observable<any> {
    let urlFilters = '';
    if (bulkOperationParams.filters.length > 0) {
      urlFilters = `${bulkOperationParams.filters}`;
    }
    const obj: IStoreBulkOperationRequest = {
      bulkOperationType: 'DELETE',
    };
    if (bulkOperationParams.items.length > 0) {
      obj.storeIds = bulkOperationParams.items.map(item => item.id);
    }
    return this.miClient.post('stores/bulkOperation' + urlFilters, obj);
  }

  getStore(id: number): Observable<IStore> {
    return  this.miClient.get<IStore>('stores/' + `${id}`);
  }

  getAllStores(pageSize: number = 1000): Observable<IStore[]> {
    const options = { params: new HttpParams({ fromObject: { pageSize: `${pageSize}` } }) };
    return this.miClient.get<IResponse>('stores', options)
      .pipe(
        map(({ list }) => list),
      );
  }

  getMyStores(username: string, params?: HttpParams): Observable<IStore[]> {
    if(!isNil(username)) {
      return this.miClient.get<IResponse>('stores?assignedTo=' + username, { params: params })
        .pipe(
          map(({ list }) => list),
        );
    }

    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      const userName = user.userName;
      if(!isNil(userName)) {
        return this.miClient.get<IResponse>('stores?assignedTo=' + userName, { params: params })
          .pipe(
            map(({ list }) => list),
          );
      }
    }

    return this.miClient.get<IResponse>('stores', { params: params })
      .pipe(
        map(({ list }) => list),
      );

  }

  getStoresByAccountName(account: string): Observable<IStore[]> {
    return  this.miClient.get<IResponse>('stores?accountName=' + encodeURIComponent(account))
    .pipe(
      map(({ list }) => list),
    );
  }

  validateAddress(address: any) {
    return this.miClient.postNoOrg('address/validate', address, false);
  }

  checkStoreNameNotTaken(name: string, excludeId: number): Observable<boolean> {
    return this.checkStoreWithFieldNotExisting('storeName', name, excludeId);
  }

  checkStoreNumberNotTaken(number: string, excludeId: number): Observable<boolean> {
    return this.checkStoreWithFieldNotExisting('storeNumber', number, excludeId);
  }

  checkStoreNicknameNotTaken(nickname: string, excludeId: number): Observable<boolean> {
    return this.checkStoreWithFieldNotExisting('nickname', nickname, excludeId);
  }

  checkStoreWithFieldNotExisting(fieldName: string, fieldValue: string, excludeId: number): Observable<boolean> {
    if (fieldValue === null || fieldValue === '' || typeof fieldValue !== 'string') {
      return of(true);
    }
    const storesUrl = 'stores?storeNameOrNumberOrNickname=' + fieldValue.trim();

    return this.miClient.get<IResponse>(storesUrl)
      .pipe(
        map(res => res.list),
        map(stores => stores.filter(store => store[fieldName].trim().toLowerCase() === fieldValue.trim().toLowerCase() && store.id !== excludeId)),
        map(stores => !stores.length),
      );
  }

  getDataForNewStoreItem(): Observable<[IStoreDesignation[], any[], any[], IStoreType[], IState[]]> {
    const storeDesignations$ = this.storeDesignationsService.getStoreDesignations();
    const divisions$ = this.miClient.get<any>('divisions');
    const countries$ = this.countryStatesService.getCountries();
    const storeTypes$ = this.storeTypeService.getStoreTypes();

    return forkJoin([storeDesignations$, divisions$, countries$, storeTypes$]).pipe(switchMap(([storeDesignations, divisions, countries, storeTypes]) => {
      const defaultCountryId = countries[0].id;
      return forkJoin([
        of(storeDesignations),
        of(divisions),
        of(countries),
        of(sortCaseInsensitive(storeTypes, 'storeType')),
        this.countryStatesService.getSortedStatesForCountry(defaultCountryId),
      ]);
    }));
  }

  getDataForStoreDetails(id: number): Observable<[
      IStore, IStoreDesignation[], string[], ICountry[], IStoreType[],IProject[], IState[], string[], IdName[]]> {
    const store$ = this.miClient.get<IStore>('stores/' + id);
    const storeDesignations$ = this.storeDesignationsService.getStoreDesignations();
    const divisions$ = this.miClient.get<any>('divisions');
    const countries$ = this.countryStatesService.getCountries();
    const storeTypes$ = this.storeTypeService.getStoreTypes();
    const projects$ = this.projectsService.getStoreProjects(id);

    return forkJoin([store$, storeDesignations$, divisions$, countries$, storeTypes$, projects$])
    .pipe(switchMap(([store, storeDesignations, divisions, countries, storeTypes, projects]) => {
      const countryId = store.countryId;
      const division = store.division;
      return forkJoin([
        of(store),
        of(storeDesignations),
        of(divisions),
        of(countries),
        of(sortCaseInsensitive(storeTypes, 'storeType')),
        of(projects),
        this.countryStatesService.getSortedStatesForCountry(countryId),
        this.usersService.getClusters(division),
        this.usersService.getFilteredRegionsAsIdName(division, store.cluster),
      ]);
    }));
  }

  getEffectiveDate( visitPeriod: string): Observable<string> {
    return this.miClient.getText('stores/effectiveDate/' + visitPeriod);
  }

  getGroupStores(groupId: number, pageSize: number = 1000): Observable<IStore[]>{
    const options = { params: new HttpParams({ fromObject: { pageSize: `${pageSize}` } }) };
    return this.miClient.get<IResponse>('stores/groupStores/' + groupId, options)
      .pipe(
        map(({ list }) => list),
      );
  }

  toSortedAccounts(stores: IStore[]): IdName[] {
    const storeAccounts = stores.map(store => ({id: store.accountId, name: store.accountName}));

    const uniqueAccounts = this.removeDuplicates(storeAccounts, item => item.id);

    return sortCaseInsensitive(uniqueAccounts, 'name');
  }

  removeDuplicates(data, key): any {
    return [
      ...new Map(data.map(item => [key(item), item])).values()
    ]
  };

  getSalesGoals(storeId: number): Observable<any> {
    return this.miClient.get<any>('stores/' + storeId + '/salesGoals');
  }
}
